/* eslint-disable no-prototype-builtins */
import { adaptV4Theme, createTheme } from '@mui/material/styles';

import {
  breakpoints,
  colorMap,
  colors,
  font,
  shape,
  spacing,
  typography
} from '../base/ui/Tokens';
import { createColorTokens } from '../base/ui/utils';

import { toState } from '../base/redux/functions';

/**
 * Extracts the fqn part from a path, where fqn represents
 * tenant/roomName.
 *
 * @param {Object} state - A redux state.
 * @returns {string}
 */
export function extractFqnFromPath(state) {
  let pathname;

  if (window.location.pathname) {
    pathname = window.location.pathname;
  } else if (state?.['features/base/connection']) {
    pathname = state['features/base/connection'].locationURL?.pathname ?? '';
  } else {
    return '';
  }

  const parts = pathname.split('/');
  const len = parts.length;

  return parts.length > 2 ? `${parts[len - 2]}/${parts[len - 1]}` : parts[1];
}

/**
 * Returns the url used for fetching dynamic branding.
 *
 * @param {Object | Function} stateful - The redux store, state, or
 * {@code getState} function.
 * @returns {string}
 */
export async function getDynamicBrandingUrl(stateful) {
  const state = toState(stateful);

  // NB: On web this is dispatched really early, before the config has been stored in the
  // state. Thus, fetch it from the window global.
  const config =
    navigator.product === 'ReactNative'
      ? state['features/base/config']
      : window.config;
  const { dynamicBrandingUrl } = config;

  if (dynamicBrandingUrl) {
    return dynamicBrandingUrl;
  }

  const { brandingDataUrl: baseUrl } = config;
  const fqn = extractFqnFromPath(state);

  if (baseUrl && fqn) {
    return `${baseUrl}?conferenceFqn=${encodeURIComponent(fqn)}`;
  }
}

/**
 * Selector used for getting the load state of the dynamic branding data.
 *
 * @param {Object} state - Global state of the app.
 * @returns {boolean}
 */
export function isDynamicBrandingDataLoaded(state) {
  return state['features/dynamic-branding'].customizationReady;
}

/**
 * Creates MUI branding theme based on the custom theme json.
 *
 * @param {Object} customTheme - The branded custom theme.
 * @returns {Object} - The MUI theme.
 */
export function createMuiBrandingTheme(customTheme) {
  const {
    palette: customPalette,
    shape: customShape,
    typography: customTypography,
    breakpoints: customBreakpoints,
    spacing: customSpacing
  } = customTheme;

  const newPalette = createColorTokens(colorMap, colors);

  if (customPalette) {
    overwriteRecurrsive(newPalette, customPalette);
  }

  const newShape = { ...shape };

  if (customShape) {
    overwriteRecurrsive(newShape, customShape);
  }

  const newTypography = {
    font: { ...font },
    ...typography
  };

  if (customTypography) {
    overwriteRecurrsive(newTypography, customTypography);
  }

  const newBreakpoints = { ...breakpoints };

  if (customBreakpoints) {
    overwriteRecurrsive(newBreakpoints, customBreakpoints);
  }

  let newSpacing = [...spacing];

  if (customSpacing?.length) {
    newSpacing = customSpacing;
  }

  return createTheme(
    adaptV4Theme({
      spacing: newSpacing,
      palette: newPalette,
      shape: newShape,
      typography: newTypography,
      breakpoints: newBreakpoints
    })
  );
}

/**
 * Overwrites recursively values from object 2 into object 1 based on common keys.
 * (Merges object2 into object1).
 *
 * @param {Object} obj1 - The object holding the merged values.
 * @param {Object} obj2 - The object to compare to and take values from.
 * @returns {void}
 */
function overwriteRecurrsive(obj1, obj2) {
  Object.keys(obj2).forEach((key) => {
    if (obj1.hasOwnProperty(key)) {
      if (typeof obj1[key] === 'object') {
        overwriteRecurrsive(obj1[key], obj2[key]);
      } else {
        obj1[key] = obj2[key];
      }
    }
  });
}
